import { Link } from "wouter";
import { ArrowRight, Home as HomeIcon, ShieldCheck, Hand, Quote, Sparkles, Clock } from "lucide-react";
import { SERVICES, TESTIMONIALS, BRAND } from "@/lib/data";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_wellness-at-home-15/artifacts/9hwlvvc5_image0.jpeg";
const ROCK_GARDEN_IMG =
  "https://customer-assets.emergentagent.com/job_wellness-at-home-15/artifacts/ay73xzg8_FB_IMG_1780415792482.jpg";

const FEATURES = [
  { icon: HomeIcon, title: "We come to you", text: "Knoxville, Maryville, Farragut & nearby. We bring the table, sheets, oils, and music." },
  { icon: ShieldCheck, title: "Licensed & insured", text: "Stephanie is a Tennessee LMT with 650+ training hours and continuing education." },
  { icon: Hand, title: "Tailored to your body", text: "Every session begins with a conversation. Pressure, focus, and pacing — your call." },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO with rock garden backdrop */}
      <section className="relative overflow-hidden">
        {/* Rock garden waterfall backdrop */}
        <div className="absolute inset-0">
          <img
            src={ROCK_GARDEN_IMG}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover scale-110 blur-md opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-spa-cream/40 via-spa-cream/25 to-spa-cream/70" />
        </div>

        <div className="relative container-spa pt-12 sm:pt-16 pb-20 sm:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 animate-fade-up">
              <p className="overline mb-6">Mobile Massage · Knoxville, TN</p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-spa-dark">
                The spa,<br />
                <span className="italic text-spa-green">brought to your door.</span>
              </h1>
              <p className="mt-8 text-lg text-spa-gray max-w-xl leading-relaxed">
                Premium therapeutic massage delivered to your home in {BRAND.serviceArea}.
                A quiet hour, on your terms — no traffic, no waiting room, no rushing back to real life.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link href="/services" data-testid="hero-book-button">
                  <button className="btn-primary">
                    Book a Session <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </Link>
                <Link href="/about" data-testid="hero-about-button">
                  <button className="btn-secondary">Meet Stephanie</button>
                </Link>
              </div>

              <div className="mt-14 flex flex-wrap items-center gap-8 text-sm font-sans text-spa-gray">
                <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-spa-cyan" strokeWidth={1.5} /> 5-star rated</div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-spa-cyan" strokeWidth={1.5} /> Same-week openings</div>
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-spa-cyan" strokeWidth={1.5} /> Licensed LMT</div>
              </div>
            </div>

            <div className="lg:col-span-6 animate-fade-in">
              <div className="relative">
                <div className="absolute -top-8 -left-8 w-40 h-40 rounded-full bg-spa-cyan/15 -z-10 hidden lg:block" />
                <div className="absolute -bottom-10 -right-6 w-56 h-56 rounded-full bg-spa-green/10 -z-10 hidden lg:block" />
                <div className="overflow-hidden rounded-[2rem] bg-spa-sand aspect-[4/5] shadow-xl">
                  <img
                    src={HERO_IMG}
                    alt="Stephanie Mason, LMT — mobile massage therapist"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="hidden sm:flex absolute -bottom-6 -left-6 bg-white border border-spa-border rounded-2xl shadow-lg px-5 py-4 items-center gap-3 max-w-xs">
                  <div className="w-10 h-10 rounded-full bg-spa-green flex items-center justify-center shrink-0">
                    <HomeIcon className="w-4 h-4 text-spa-cream" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-serif text-base text-spa-dark leading-tight">In-home sessions</p>
                    <p className="text-xs text-spa-gray mt-0.5">60 / 90 / 120 minute appointments</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-spa-sand">
        <div className="container-spa section-padding">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {FEATURES.map((f) => (
              <div key={f.title} data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`} className="card-spa">
                <f.icon className="w-7 h-7 text-spa-green mb-5" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl text-spa-dark mb-3">{f.title}</h3>
                <p className="text-spa-gray leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES HIGHLIGHT */}
      <section className="bg-spa-cream">
        <div className="container-spa section-padding">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <p className="overline mb-4">Our Practice</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-spa-dark leading-[1.05]">
                A session shaped<br />around your body.
              </h2>
            </div>
            <Link href="/services" data-testid="home-view-all-services">
              <button className="btn-secondary self-start">
                View all services <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Link key={s.id} href="/services" data-testid={`home-service-card-${s.id}`}>
                <div className={`card-spa cursor-pointer group h-full flex flex-col ${i === 0 ? "md:col-span-2 lg:col-span-1" : ""}`}>
                  <div className="flex items-center justify-between mb-5">
                    <span className="overline">{s.durations[0].label} · ${s.durations[0].price}</span>
                    <ArrowRight className="w-4 h-4 text-spa-gray group-hover:text-spa-green group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl text-spa-dark mb-3">{s.name}</h3>
                  <p className="text-spa-gray leading-relaxed text-sm flex-1">{s.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-spa-sand">
        <div className="container-spa section-padding">
          <div className="max-w-3xl mb-16">
            <p className="overline mb-4">From our clients</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-spa-dark leading-[1.05]">
              Quiet hours, deeply felt.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div key={t.name} data-testid={`testimonial-${i}`} className="bg-white rounded-2xl p-8 border border-spa-border">
                <Quote className="w-7 h-7 text-spa-cyan mb-5" strokeWidth={1.5} />
                <p className="font-serif text-xl text-spa-dark italic leading-relaxed mb-6">&ldquo;{t.quote}&rdquo;</p>
                <div className="pt-5 border-t border-spa-border">
                  <p className="font-sans text-sm font-medium text-spa-dark">{t.name}</p>
                  <p className="text-xs text-spa-gray mt-0.5">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-spa-green text-spa-cream">
        <div className="container-spa section-padding text-center">
          <p className="overline text-spa-gold mb-6">Ready when you are</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl mx-auto">
            Your home. Your hour. Your reset.
          </h2>
          <p className="mt-8 text-spa-cream/70 max-w-xl mx-auto leading-relaxed">
            Same-week appointments often available. Gift cards delivered instantly.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link href="/services" data-testid="cta-book-now">
              <button className="btn-accent">Book Now <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></button>
            </Link>
            <Link href="/gift-cards" data-testid="cta-gift-card">
              <button className="bg-transparent border border-spa-cream/40 text-spa-cream px-8 py-3.5 rounded-full font-medium hover:bg-spa-cream/10 transition-all">
                Send a gift card
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
